/**
 * Ajuda contextual.
 *
 * Tres formatos, com propositos diferentes e deliberadamente discretos — a
 * ajuda existe para quem precisa dela e nao deve competir com o conteudo da
 * tela:
 *
 *   HelpLink  — "Como funciona?" ao lado de um titulo ou campo. Leva ao trecho
 *               exato da Central de Ajuda.
 *   HelpTip   — icone de interrogacao com explicacao curta na propria tela,
 *               para o que cabe em duas linhas.
 *   HelpPanel — bloco de orientacao no topo de uma area, quando a tela inteira
 *               depende de um conceito.
 */

import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HELP_POINTS, type HelpPointKey, helpPath } from '../help/registry';
import { IconAlert } from './ui';

/** Interrogação em círculo. Só aqui, para não poluir o catálogo de ícones. */
function IconHelp({ className = 'h-4 w-4' }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="7.25" />
      <path d="M8.1 7.6a1.95 1.95 0 1 1 2.9 1.7c-.6.35-1 .78-1 1.45v.35" />
      <path d="M10 14.2h.01" />
    </svg>
  );
}

/**
 * Link para o trecho da Central de Ajuda que explica este ponto da tela.
 */
export function HelpLink({
  point,
  label,
  className = '',
}: {
  point: HelpPointKey;
  label?: string;
  className?: string;
}): JSX.Element {
  const target = HELP_POINTS[point];
  return (
    <Link
      to={helpPath(point)}
      // `help-link` acrescenta o alvo de toque de 44 px em tela pequena e em
      // ponteiro grosso (BKC-009). Este link fica isolado abaixo do subtítulo,
      // não no meio de um parágrafo, então a exceção de "link em texto corrido"
      // da WCAG 2.5.5 não se aplica a ele.
      className={`help-link inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline ${className}`}
    >
      <IconHelp className="h-3.5 w-3.5 shrink-0" />
      {label ?? target.label}
    </Link>
  );
}

/**
 * Explicação curta ali mesmo, sem sair da tela. Abre no clique (não no hover):
 * hover não existe em tablet e some antes de a pessoa terminar de ler.
 */
export function HelpTip({
  text,
  point,
  label = 'Ajuda',
}: {
  text: string;
  point?: HelpPointKey;
  label?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        className="btn-ghost -my-1 h-6 w-6 rounded-full p-0 text-ink-subtle hover:text-brand-700"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((current) => !current)}
      >
        <IconHelp className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className="absolute left-1/2 top-7 z-30 w-64 -translate-x-1/2 rounded-md border border-line bg-surface p-2.5 text-xs leading-relaxed text-ink-muted shadow-pop"
        >
          {text}
          {point !== undefined && (
            <span className="mt-1.5 block">
              <HelpLink point={point} />
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/**
 * Orientação no topo de uma área. Use quando a tela inteira depende de um
 * conceito — não para repetir o que os campos já dizem.
 */
export function HelpPanel({
  title,
  children,
  point,
  tone = 'info',
}: {
  title: string;
  children: ReactNode;
  point?: HelpPointKey;
  tone?: 'info' | 'warning';
}): JSX.Element {
  const styles =
    tone === 'warning'
      ? 'border-warning-200 bg-warning-50 text-warning-700'
      : 'border-brand-100 bg-brand-50 text-ink-muted';

  return (
    <div className={`mb-4 flex gap-2.5 rounded-md border px-3 py-2.5 ${styles}`}>
      <div className="shrink-0 pt-0.5 text-brand-600">
        {tone === 'warning' ? <IconAlert className="h-4 w-4" /> : <IconHelp className="h-4 w-4" />}
      </div>
      <div className="min-w-0 text-sm">
        <p className="font-medium text-ink">{title}</p>
        <div className="mt-0.5 leading-relaxed">{children}</div>
        {point !== undefined && (
          <p className="mt-1.5">
            <HelpLink point={point} />
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Sugestão do próximo passo, para o fim de um fluxo. Uma tela que termina sem
 * dizer o que fazer depois deixa a pessoa parada.
 */
export function NextStepHint({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-surface-sunken px-3 py-2.5 text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between">
      <p className="min-w-0">
        <span className="font-medium text-ink">Próximo passo: </span>
        {children}
      </p>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export { IconHelp };
